import React from 'react';
import ReactDOM from 'react-dom';

import styled from '@emotion/styled';
import { Button } from '@lightspeed/flame/Button';
import { themeGet } from '@lightspeed/flame/Core';
import { IconChevronDown } from '@lightspeed/flame/Icon/ChevronDown';
import { IconChevronRight } from '@lightspeed/flame/Icon/ChevronRight';

const StyledButton = styled(Button)`
  background: transparent;
  border: none;
  justify-content: flex-start;
  padding: ${themeGet('space.2')};
`;

interface PartitionCollapseToggleViewInnerProps {
  partitionName: string;
  title: string;
  isCollapsed?: boolean;
  onToggle?: (collapsed: boolean) => void;
}

export const PartitionCollapseToggleViewInner: React.FC<PartitionCollapseToggleViewInnerProps> = ({
  partitionName,
  title,
  isCollapsed,
  onToggle = () => {},
}) => {
  const [collapsed, setCollapsed] = React.useState(isCollapsed ?? false);
  const [renderTarget, setRenderTarget] = React.useState<HTMLDivElement | null>(null);

  const partitionElement = React.useRef(
    document.querySelector(`[data-partition="${partitionName}"]`)
  );

  // Initialize UI
  React.useEffect(() => {
    const targetDOM = partitionElement.current;
    if (!targetDOM) return () => {};

    targetDOM.classList.add('collapsable-partition');

    const div = document.createElement('div');
    targetDOM.parentNode?.insertBefore(div, partitionElement.current);

    setRenderTarget(div);

    return () => {
      targetDOM.parentNode?.removeChild(div);
    };
  }, [partitionElement]);

  // Handle user interaction
  React.useEffect(() => {
    if (!partitionElement.current) return;
    if (!renderTarget) return;

    partitionElement.current.classList.toggle('hidden', collapsed);
  }, [collapsed, renderTarget, partitionElement]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    setCollapsed(!collapsed);
    onToggle(!collapsed);
  };

  // Render
  if (!partitionElement.current) return null;
  if (!renderTarget) return null;

  const className = `partition-collapse-toggle ${collapsed ? 'collapsed' : ''}`;

  return ReactDOM.createPortal(
    <div className={className} data-test="partition-toggle-inner">
      <StyledButton
        onClick={handleClick}
        block={true}
        fill={false}
        size="large"
        data-test="partition-toggle-inner-button"
      >
        {collapsed ? <IconChevronRight /> : <IconChevronDown />}
        {title}
      </StyledButton>
    </div>,
    renderTarget
  );
};
